'use client'

import Image, { ImageLoaderProps, ImageProps } from 'next/image'
import React from 'react'

import { decode, isBlurhashValid } from 'blurhash'
export type CustomImageProps = ImageProps & {
  disableAutoFormat?: boolean
  blurHash?: string
  className?: string
  forceDefaultLoader?: boolean
}

/**
 * ImageWithCustomLoader
 *
 * A thin wrapper around Next.js' `<Image>` that automatically picks the
 * appropriate loader depending on the source:
 *
 * 1. **Static imports / objects**
 *    When `props.src` is **not a string** (i.e. the result of
 *    `import img from './file.png'`) we simply forward all props to `next/image`
 *    unchanged. The built-in static-import optimisation already provides
 *    `blurDataURL`, so no extra work is needed.
 *
 * 2. **Local runtime paths (`/something.png`)**
 *    If `props.src` *is* a string that **starts with "**", we assume the asset
 *    will be served from the same origin. We still use the default Next.js
 *    loader, but we manually inject:
 *      • `placeholder="blur"` / `blurDataURL` — generated from the optional
 *        `blurHash` prop, because runtime paths don't get this automatically.
 *
 * 3. **External URLs**
 *    All other string sources are treated as remote images.
 *    Unless the caller supplied its own `props.loader`, we fall back to
 *    `cloudinaryLoader`, leveraging Cloudinary's image-fetch endpoint for
 *    resizing, quality selection, and (unless `disableAutoFormat` is true)
 *    automatic format conversion.
 *
 * Extra props:
 * • `blurHash` – (Base64-encoded blurhash) will be decoded into a 32 × 32 PNG
 *   and applied as `blurDataURL`.
 * • `disableAutoFormat` – when true, omits the `f_auto` parameter from the
 *   Cloudinary URL so the original format is preserved.
 * • `className` – forwarded to the rendered `<Image>`.
 *
 * All other `ImageProps` are passed through untouched.
 *
 * The helper also calculates a sensible default `sizes` value (`100vw`) when
 * neither `width`, `height`, nor `sizes` are provided and `fill` is true.
 */
export const ImageWithCustomLoader = ({
  disableAutoFormat = false,
  blurHash,
  className,
  forceDefaultLoader = false,
  ...props
}: CustomImageProps): React.ReactElement | null => {
  const defaultSizes = props.width || props.height ? undefined : `100vw`
  const blurDataURL = blurHashToDataURL(blurHash)
  const srcString = getSrcString(props.src)

  if (typeof props.src !== 'string') {
    return <Image {...props} className={className} sizes={props.fill && !props.sizes ? defaultSizes : props.sizes} />
  }

  if (srcString.startsWith('/') || forceDefaultLoader) {
    return (
      <Image
        {...props}
        className={className}
        sizes={props.fill && !props.sizes ? defaultSizes : props.sizes}
        placeholder={blurDataURL ? 'blur' : 'empty'}
        blurDataURL={blurDataURL}
      />
    )
  }

  return (
    <Image
      className={className}
      loader={(loaderProps) => {
        if (props.loader) {
          return props.loader(loaderProps)
        }

        return cloudinaryLoader(disableAutoFormat ? disableAutoFormat : false, loaderProps)
      }}
      placeholder={blurDataURL ? 'blur' : 'empty'}
      blurDataURL={blurDataURL}
      sizes={props.fill && !props.sizes ? defaultSizes : props.sizes}
      {...props}
    />
  )
}

function getSrcString(src: CustomImageProps['src']): string {
  if (typeof src === 'string') {
    return src
  }

  if ('src' in src) {
    return src.src
  }

  return src.default.src
}

const normalizeSrc = (src: string): string => {
  return src[0] === '/' ? src.slice(1) : src
}

export const cloudinaryLoader = (disableAutoFormat: boolean, { src, width, quality }: ImageLoaderProps): string => {
  // DEMO: https://res.cloudinary.com/art-blocks/image/fetch/w_300,c_limit,q_auto/https://d2ekshiy7r5vl7.cloudfront.net/7515.png
  const params = ['f_auto', 'c_limit', 'w_' + width, 'q_' + (quality || 'auto')]
  if (disableAutoFormat) {
    params.shift()
  }
  const paramsString = params.join(',') + '/'
  return `https://res.cloudinary.com/art-blocks/image/fetch/${paramsString}${normalizeSrc(src)}`
}

// Code taken and adjusted from https://gist.github.com/mattiaz9/53cb67040fa135cb395b1d015a200aff
// Steps: Get UInt8 array from blurhash, convert to png, base64 encode png
export function blurHashToDataURL(hash: string | undefined): string | undefined {
  if (!hash) return undefined
  const parsedHash = Buffer.from(hash ?? '', 'base64').toString('utf8')
  if (!isBlurhashValid(parsedHash).result) {
    return undefined
  }
  const pixels = decode(parsedHash, 32, 32)
  const dataURL = parsePixels(pixels, 32, 32)
  return dataURL
}

// thanks to https://github.com/wheany/js-png-encoder
function parsePixels(pixels: Uint8ClampedArray, width: number, height: number) {
  const pixelsString = [...pixels].map((byte) => String.fromCharCode(byte)).join('')
  const pngString = generatePng(width, height, pixelsString)
  const dataURL =
    typeof Buffer !== 'undefined' ? Buffer.from(getPngArray(pngString)).toString('base64') : btoa(pngString)
  return 'data:image/png;base64,' + dataURL
}

function getPngArray(pngString: string) {
  const pngArray = new Uint8Array(pngString.length)
  for (let i = 0; i < pngString.length; i++) {
    pngArray[i] = pngString.charCodeAt(i)
  }
  return pngArray
}

function generatePng(width: number, height: number, rgbaString: string) {
  const DEFLATE_METHOD = String.fromCharCode(0x78, 0x01)
  const CRC_TABLE: number[] = []
  const SIGNATURE = String.fromCharCode(137, 80, 78, 71, 13, 10, 26, 10)
  const NO_FILTER = String.fromCharCode(0)

  let n, c, k

  // make crc table
  for (n = 0; n < 256; n++) {
    c = n
    for (k = 0; k < 8; k++) {
      if (c & 1) {
        c = 0xedb88320 ^ (c >>> 1)
      } else {
        c = c >>> 1
      }
    }
    CRC_TABLE[n] = c
  }

  // Functions
  function inflateStore(data: string) {
    const MAX_STORE_LENGTH = 65535
    let storeBuffer = ''
    let remaining
    let blockType

    for (let i = 0; i < data.length; i += MAX_STORE_LENGTH) {
      remaining = data.length - i
      blockType = ''

      if (remaining <= MAX_STORE_LENGTH) {
        blockType = String.fromCharCode(0x01)
      } else {
        remaining = MAX_STORE_LENGTH
        blockType = String.fromCharCode(0x00)
      }
      // little-endian
      storeBuffer += blockType + String.fromCharCode(remaining & 0xff, (remaining & 0xff00) >>> 8)
      storeBuffer += String.fromCharCode(~remaining & 0xff, (~remaining & 0xff00) >>> 8)

      storeBuffer += data.substring(i, i + remaining)
    }

    return storeBuffer
  }

  function adler32(data: string) {
    const MOD_ADLER = 65521
    let a = 1
    let b = 0

    for (let i = 0; i < data.length; i++) {
      a = (a + data.charCodeAt(i)) % MOD_ADLER
      b = (b + a) % MOD_ADLER
    }

    return (b << 16) | a
  }

  function updateCrc(crc: number, buf: string) {
    let c = crc
    let b: number

    for (let n = 0; n < buf.length; n++) {
      b = buf.charCodeAt(n)
      c = CRC_TABLE[(c ^ b) & 0xff] ^ (c >>> 8)
    }
    return c
  }

  function crc(buf: string) {
    return updateCrc(0xffffffff, buf) ^ 0xffffffff
  }

  function dwordAsString(dword: number) {
    return String.fromCharCode(
      (dword & 0xff000000) >>> 24,
      (dword & 0x00ff0000) >>> 16,
      (dword & 0x0000ff00) >>> 8,
      dword & 0x000000ff
    )
  }

  function createChunk(length: number, type: string, data: string) {
    const CRC = crc(type + data)

    return dwordAsString(length) + type + data + dwordAsString(CRC)
  }

  function createIHDR(width: number, height: number) {
    const IHDRdata =
      dwordAsString(width) +
      dwordAsString(height) +
      // bit depth
      String.fromCharCode(8) +
      // color type: 6=truecolor with alpha
      String.fromCharCode(6) +
      // compression method: 0=deflate, only allowed value
      String.fromCharCode(0) +
      // filtering: 0=adaptive, only allowed value
      String.fromCharCode(0) +
      // interlacing: 0=none
      String.fromCharCode(0)

    return createChunk(13, 'IHDR', IHDRdata)
  }

  // PNG creations

  const IEND = createChunk(0, 'IEND', '')
  const IHDR = createIHDR(width, height)

  let scanlines = ''
  let scanline

  for (let y = 0; y < rgbaString.length; y += width * 4) {
    scanline = NO_FILTER
    if (Array.isArray(rgbaString)) {
      for (let x = 0; x < width * 4; x++) {
        scanline += String.fromCharCode(rgbaString[y + x] & 0xff)
      }
    } else {
      scanline += rgbaString.substr(y, width * 4)
    }
    scanlines += scanline
  }

  const compressedScanlines = DEFLATE_METHOD + inflateStore(scanlines) + dwordAsString(adler32(scanlines))
  const IDAT = createChunk(compressedScanlines.length, 'IDAT', compressedScanlines)

  const pngString = SIGNATURE + IHDR + IDAT + IEND
  return pngString
}
